import currencyFormatter from '../formatters/priceFormatter';
import ramiturvaPriceFormatter from '../formatters/ramiturvaPriceFormatter';
import percentFormatter from '../formatters/percentFormatter';
import {
  CellClassParams,
  ICellEditorParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import {
  Nullable,
  OfferItem,
  PricingSheetRow,
  RoundingBasis,
} from '../../../../../shared/types';
import {
  ContractType,
  DatabaseItemGroup,
} from '../../../../../shared/types/offers';
import { calculateTotalPrice } from '../../../../../shared/calculateTotalPrice';
import {
  getClassForAgGridCell,
  getOfferExternalComparisonPriceValue,
} from '../../../helpers/cellHelpers';
import { isMonthPricing } from '../../../pages/OfferSheets/helpers';
import isGroupHeader from '../../../helpers/isGroupHeader';
import NumericEditorV2 from '../Editors/NumericEditorV2';
import PriceRenderer from '../Renderers/PriceRenderer';
import { ProposalPriceRenderer } from '../Renderers/ProposalPriceRenderer';
import { OfferPricingBasisRenderer } from '../Renderers/OfferPricingBasisRenderer';
import proposalMonthPriceGetter from '../valueGetters/proposalMonthPriceGetter';
import comparisonPriceValueGetter from '../valueGetters/comparisonPriceValueGetter';
import { priceGetter } from '../valueGetters/priceGetter';
import discountGetter from '../valueGetters/discountGetter';
import proposalDayPriceGetter from '../valueGetters/proposalDayPriceGetter';
import dayPricePercentGetter from '../valueGetters/dayPricePercentGetter';
import monthPricePercentGetter from '../valueGetters/monthPricePercentGetter';
import { rentalPeriodValueGetter } from '../valueGetters/rentalPeriodValueGetter';
import { pricingBasisValueGetter } from '../valueGetters/pricingBasisValueGetter';
import {
  discountAggregator,
  priceAggregator,
  proposalPriceAggregator,
  ramiturvaAggregator,
  rentPeriodTotalPriceAggregator,
  singlePaymentAggregator,
  totalPriceAggregator,
} from '../aggregators/aggregatorFunctions';
import MultiLineHeader from '../Components/MultiLineHeader';
import { isSalesItem } from '../../../../../shared/helpers/isSalesItem';
import { roundToTwoDecimal } from '../../../../../shared/roundToTwoDecimal';
import { ramiturvaPriceGetter } from '../valueGetters/RamiturvaPricegetter';
import { hasRamiturva } from '../../../../../shared/helpers/hasRamiturva';
import { GridColumnProps } from './ColumnsBase';
import { singlePaymentPriceGetter } from '../valueGetters/singlePaymentValueGetter';
import contractPriceValueGetter from '../valueGetters/contractPriceValueGetter';

interface OptionalColumnProps {
  roundingBasisSheet: RoundingBasis;
  contractType: ContractType;
  handleUpdateRowValue: (row: PricingSheetRow) => void;
  handleMultipleRowValueUpdates: (row: PricingSheetRow[]) => void;
  handleItemGroupUpdate?: (itemGroup: DatabaseItemGroup) => void;
}

const defaultCellStyle = {
  borderLeft: '1px solid #ccc',
  display: 'flex',
};

const editableCellStyle = {
  color: '#000',
};

const ColumnsOffer = ({
  classes,
  optionalColumnProps,
}: GridColumnProps<OptionalColumnProps>) => {
  const {
    handleUpdateRowValue,
    handleMultipleRowValueUpdates,
    handleItemGroupUpdate,
    roundingBasisSheet,
    contractType,
  } = optionalColumnProps;

  const isSinglePayment = contractType === ContractType.SinglePayment;

  return [
    {
      field: 'itemGroup.groupName',
      hide: true,
      rowGroup: true,
      editable: true,
    },
    {
      field: 'rentalPeriod',
      headerName: 'Vuokra-aika',
      minWidth: 160,
      filter: false,
      sortable: false,
      valueGetter: rentalPeriodValueGetter,
      cellStyle: {
        ...defaultCellStyle,
      },
    },
    {
      field: 'pricingBasis',
      headerName: 'Hinnoitteluperuste',
      filter: false,
      cellRenderer: OfferPricingBasisRenderer,
      valueGetter: pricingBasisValueGetter,
      valueFormatter: (params: ValueFormatterParams<OfferItem>) => {
        if (
          isSalesItem(params.data?.type) ||
          !params.data ||
          params.data.pricingBasis === undefined
        ) {
          return null;
        }
        return params.data.pricingBasis;
      },
      cellRendererParams: {
        handleUpdateRowValue,
        handleItemGroupUpdate,
        handleMultipleRowValueUpdates,
        contractType,
      },
      cellClass: (params: CellClassParams) =>
        getClassForAgGridCell(params.data, classes, 'pricingBasis'),
      cellStyle: { ...defaultCellStyle, padding: 0 },
      headerTooltip: 'Onko tuotteella PV vai KK hinta',
      maxWidth: 150,
    },
    {
      field: 'quantity',
      headerName: 'Lkm',
      editable: true,
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<OfferItem>) => {
        if (isGroupHeader(params)) {
          return params.node?.allLeafChildren[0].data?.itemGroup
            ?.quantity;
        } else {
          return params.data?.quantity;
        }
      },
      cellEditor: NumericEditorV2,
      cellEditorParams: {
        handleUpdateRowValue,
        allowNegative: false,
        allowZero: false,
        required: false,
        handleItemGroupUpdate,
      },
      maxWidth: 80,
      headerTooltip: 'Tuotteiden lukumäärä',
      cellClass: (params: CellClassParams) =>
        getClassForAgGridCell(params.data, classes, 'quantity'),
      cellStyle: {
        ...defaultCellStyle,
        ...editableCellStyle,
      },
    },
    {
      field: 'proposalPrice',
      headerName: 'Ehdotushinta',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<OfferItem>) => {
        if (isMonthPricing(params)) {
          return proposalMonthPriceGetter(params);
        } else {
          return proposalDayPriceGetter(params);
        }
      },
      valueFormatter: currencyFormatter,
      cellRenderer: ProposalPriceRenderer,
      headerTooltip: 'Työkalun ehdottama hinta',
      cellStyle: defaultCellStyle,
      aggFunc: proposalPriceAggregator,
    },
    {
      field: 'price',
      headerName: 'Tarjouksen hinta',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<OfferItem>) => {
        return priceGetter(params, roundingBasisSheet);
      },
      valueFormatter: currencyFormatter,
      editable:
        contractType === ContractType.SinglePayment ? false : true,
      headerTooltip: 'Tuotteen hinta',
      cellEditor: NumericEditorV2,
      cellEditorParams: (params: ICellEditorParams) => {
        const commonHandlers = {
          handleItemGroupUpdate,
          handleUpdateRowValue,
          handleMultipleRowValueUpdates,
        };

        if (isGroupHeader(params)) {
          return commonHandlers;
        }

        const {
          data: { pricingBasis },
          column,
        } = params;
        const columnId = column.getColId();

        const defaultField =
          columnId === 'price'
            ? pricingBasis === 'MONTH'
              ? 'monthPrice'
              : 'dayPrice'
            : undefined;

        return {
          ...commonHandlers,
          defaultField,
        };
      },
      cellClass: (params: CellClassParams) => {
        const priceType = isMonthPricing(params)
          ? 'monthPrice'
          : 'dayPrice';
        return getClassForAgGridCell(params.data, classes, priceType);
      },
      cellRenderer: PriceRenderer,
      cellRendererParams: (params: ICellEditorParams<OfferItem>) => {
        if (isGroupHeader(params)) return '';
        const priceType = isMonthPricing(params) ? 'MONTH' : 'DAY';

        return {
          contractPrice: getOfferExternalComparisonPriceValue(
            params.data,
            'contract',
            priceType,
          ),
          minPrice: isMonthPricing(params)
            ? params.data.minMonthPrice
            : params.data.minDayPrice,
        };
      },
      cellStyle: isSinglePayment
        ? defaultCellStyle
        : editableCellStyle,
      aggFunc: priceAggregator,
    },
    {
      field: 'totalPrice',
      headerName: 'Kokonaishinta',
      filter: 'agNumberColumnFilter',
      editable: false,
      valueGetter: (params: ValueGetterParams<OfferItem>) => {
        return roundToTwoDecimal(
          (priceGetter(
            params,
            optionalColumnProps.roundingBasisSheet,
          ) as number) * (params.data?.quantity ?? 1),
        );
      },
      aggFunc: totalPriceAggregator,
      valueFormatter: currencyFormatter,
      headerTooltip:
        'Tuotteen yhteenlaskettu hinta vuokra-aikaa huomioimatta',
      cellStyle: defaultCellStyle,
    },
    {
      field: 'rentPeriodTotalPrice',
      headerName: 'Vuokra-ajan kokonaishinta',
      filter: 'agNumberColumnFilter',
      editable: false,
      headerComponent: MultiLineHeader,
      headerComponentParams: {
        header1: 'Vuokra-ajan',
        header2: 'kokonaishinta',
      },
      valueGetter: (params: ValueGetterParams<OfferItem>) => {
        return !isSalesItem(params.data?.type)
          ? calculateTotalPrice(
              params.data,
              optionalColumnProps.roundingBasisSheet,
            )
          : null;
      },
      cellEditor: NumericEditorV2,
      cellEditorParams: {
        handleUpdateRowValue,
        handleMultipleRowValueUpdates,
      },

      valueFormatter: currencyFormatter,
      headerTooltip:
        'Tuotteen yhteenlaskettu hinta vuokra-aika huomioiden',
      cellStyle: defaultCellStyle,
      aggFunc: rentPeriodTotalPriceAggregator,
    },
    {
      field: 'singlePaymentPrice',
      headerName: 'Single payment hinta',
      filter: 'agNumberColumnFilter',
      editable: true,
      headerComponent: MultiLineHeader,
      cellStyle: {
        ...editableCellStyle,
        borderLeft: '1px solid #ccc',
      },
      hide: !isSinglePayment,
      headerComponentParams: {
        header1: 'Single payment',
        header2: 'hinta',
      },
      cellEditor: NumericEditorV2,
      cellEditorParams: {
        handleItemGroupUpdate,
        handleUpdateRowValue,
        handleMultipleRowValueUpdates,
      },
      valueGetter: (params: ValueGetterParams<OfferItem>) =>
        singlePaymentPriceGetter(
          params,
          optionalColumnProps.roundingBasisSheet,
        ),
      cellClass: (params: CellClassParams) => {
        return getClassForAgGridCell(
          params.data,
          classes,
          'singlePaymentPrice',
        );
      },
      aggFunc: singlePaymentAggregator,
      headerTooltip:
        'Tuotteen yhteenlaskettu hinta vuokra-aika huomioiden',
    },
    {
      field: 'discount',
      headerName: 'Alennus%',
      filter: 'agNumberColumnFilter',
      valueGetter: discountGetter,
      valueFormatter: percentFormatter,
      editable: isSinglePayment ? false : true,
      headerTooltip: 'Hinta verrattuna ehdotushintaan',
      cellEditor: NumericEditorV2,
      cellEditorParams: {
        handleUpdateRowValue,
        handleMultipleRowValueUpdates,
      },
      cellClass: (params: CellClassParams) => {
        const discountType = isMonthPricing(params)
          ? 'discountMonth'
          : 'discountDay';
        return getClassForAgGridCell(
          params.data,
          classes,
          discountType,
        );
      },
      cellStyle: isSinglePayment
        ? defaultCellStyle
        : {
            ...defaultCellStyle,
            ...editableCellStyle,
          },
      aggFunc: discountAggregator,
    },
    {
      field: 'ramiturvaPrice',
      headerName: 'RamiTurvan hinta',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<OfferItem>) =>
        ramiturvaPriceGetter(
          params,
          optionalColumnProps.roundingBasisSheet,
        ),
      valueFormatter: ramiturvaPriceFormatter,
      editable: (params: CellClassParams) =>
        hasRamiturva(params.data),
      headerTooltip: 'RamiTurvan hinta',
      cellEditor: NumericEditorV2,
      cellEditorParams: (params: ICellEditorParams) => {
        const {
          data: { pricingBasis },
          column,
        } = params;
        const columnId = column.getColId();

        let defaultField;
        if (columnId === 'ramiturvaPrice') {
          if (pricingBasis === 'DAY') {
            defaultField = 'ramiturvaDayPrice';
          } else if (pricingBasis === 'MONTH') {
            defaultField = 'ramiturvaMonthPrice';
          } else {
            defaultField = '';
          }
        }
        return { handleUpdateRowValue, defaultField };
      },
      cellClass: (params: CellClassParams) => {
        return getClassForAgGridCell(
          params.data,
          classes,
          'ramiturvaPrice',
        );
      },
      cellStyle: {
        ...editableCellStyle,
        borderLeft: '1px solid #ccc',
      },
      aggFunc: ramiturvaAggregator,
    },
    {
      field: 'contractPrice',
      headerName: 'Sopimushinta',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<OfferItem>) => {
        const priceType = isMonthPricing(params) ? 'MONTH' : 'DAY';
        return contractPriceValueGetter(
          params,
          optionalColumnProps.roundingBasisSheet,
          getOfferExternalComparisonPriceValue(
            params.data,
            'contract',
            priceType,
          ),
        );
      },
      valueFormatter: currencyFormatter,
      cellStyle: defaultCellStyle,
      suppressColumnsToolPanel: false,
    },
    {
      field: 'contractPricePercent',
      headerName: 'SopEro%',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<OfferItem>) => {
        if (!params.data) return '';
        let pricePercent;
        const priceType = isMonthPricing(params) ? 'MONTH' : 'DAY';
        priceType === 'MONTH'
          ? (pricePercent = monthPricePercentGetter(
              params,
              getOfferExternalComparisonPriceValue(
                params.data,
                'contract',
                priceType,
              ),
            ))
          : (pricePercent = dayPricePercentGetter(
              params,
              getOfferExternalComparisonPriceValue(
                params.data,
                'contract',
                priceType,
              ),
            ));
        return pricePercent;
      },
      valueFormatter: (params: {
        value: Nullable<number>;
        data: PricingSheetRow | undefined;
      }) => {
        if (!params.value) return '';
        return `${params.value} %`;
      },
      cellStyle: {
        ...defaultCellStyle,
        borderRight: '1px solid #ccc',
      },
      suppressColumnsToolPanel: false,
      hide:
        localStorage.getItem(
          'columnVisibility_contractPricePercent',
        ) === 'false',
    },
    {
      field: 'comparisonPrice',
      headerName: 'VRThinta',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<OfferItem>) => {
        const priceType = isMonthPricing(params) ? 'MONTH' : 'DAY';
        return comparisonPriceValueGetter(
          params,
          optionalColumnProps.roundingBasisSheet,
          getOfferExternalComparisonPriceValue(
            params.data,
            'comparison',
            priceType,
          ),
        );
      },
      valueFormatter: currencyFormatter,
      headerTooltip: 'Tuotteen hinta valitussa vertailuhinnastossa',
      cellStyle: {
        ...defaultCellStyle,
        borderRight: '1px solid #ccc',
      },
    },
    {
      field: 'comparisonPricePercent',
      headerName: 'VRTero%',
      filter: 'agNumberColumnFilter',
      valueGetter: (params: ValueGetterParams<OfferItem>) => {
        if (!params.data) return '';
        let pricePercent;
        const priceType = isMonthPricing(params) ? 'MONTH' : 'DAY';
        priceType === 'MONTH'
          ? (pricePercent = monthPricePercentGetter(
              params,
              getOfferExternalComparisonPriceValue(
                params.data,
                'comparison',
                priceType,
              ),
            ))
          : (pricePercent = dayPricePercentGetter(
              params,
              getOfferExternalComparisonPriceValue(
                params.data,
                'comparison',
                priceType,
              ),
            ));
        return pricePercent;
      },
      valueFormatter: (params: {
        value: Nullable<number>;
        data: PricingSheetRow | undefined;
      }) => {
        if (!params.value) return '';
        return `${params.value} %`;
      },
      cellStyle: {
        ...defaultCellStyle,
        borderRight: '1px solid #ccc',
      },
      suppressColumnsToolPanel: false,
      hide:
        localStorage.getItem(
          'columnVisibility_comparisonPricePercent',
        ) === 'false',
    },
    {
      field: 'comment',
      headerName: 'Riviselite',
      tooltipField: 'comment',
      minWidth: 200,
    },
  ];
};

export default ColumnsOffer;
